import { useCallback, useRef, useState } from 'react'
import type { AssetListAsset } from './asset-list-card.types'

/** What one run of downloads reported. */
export interface DownloadOutcome {
	/** How many downloads were started - started, not completed. */
	startedCount: number
	/** The names of the assets whose download could not be started. */
	failedFileNames: string[]
}

/**
 * How a host downloads a whole selection, reporting what it managed to start.
 *
 * The SELECTION rather than one asset, because everything that decides whether N downloads
 * succeed is the host's: requesting each credential, the order the browser is handed them in,
 * and the spacing between them (a round trip between two hand-offs spends the transient user
 * activation the button press granted, after which the browser may suppress the rest silently).
 * A card that looped one asset at a time would be making those decisions on the host's behalf.
 */
export type DownloadAssets = (assets: AssetListAsset[]) => Promise<DownloadOutcome>

export interface AssetListDownloadState {
	/** Download every asset given, in order. Ignored while a run is in flight. */
	download: (assets: AssetListAsset[]) => Promise<void>
	isPending: boolean
	/** How many downloads the last run started - started, not completed. */
	startedCount: number
	/** The names of the assets whose download could not be started, so a partial result names what is missing. */
	failedFileNames: string[]
	/** True only when nothing at all could be started, so a total failure is not reported as a partial one. */
	hasFailed: boolean
	/** Drop the last run's outcome - for when the selection it described no longer exists. */
	reset: () => void
}

/**
 * The card's side of a bulk download: one run at a time, and one report of what it started.
 *
 * The host owns what a download IS - the credential, the transport, the hand-off to the
 * browser - so nothing about any of that is decided here. What is decided here is the part
 * every host would otherwise reimplement: that an asset listed twice is downloaded once, that
 * a run which throws outright is still reported rather than left silent, and that a started
 * download is all this ever claims, because once the browser owns a transfer its outcome is
 * not observable.
 *
 * Re-entry is blocked through a ref rather than the `isPending` state, because two clicks in
 * one tick would both read the pre-update state and fan out into a duplicate run.
 */
export function useAssetListDownload(onDownloadAssets?: DownloadAssets): AssetListDownloadState {
	const [isPending, setIsPending] = useState(false)
	const [startedCount, setStartedCount] = useState(0)
	const [failedFileNames, setFailedFileNames] = useState<string[]>([])
	const [hasFailed, setHasFailed] = useState(false)
	const running = useRef(false)

	const reset = useCallback(() => {
		setStartedCount(0)
		setFailedFileNames([])
		setHasFailed(false)
	}, [])

	const download = useCallback(
		async (assets: AssetListAsset[]) => {
			if (!onDownloadAssets || running.current) return

			// One entry per asset: an asset listed under two roles is one asset, and must not
			// be requested - or downloaded - twice from one selection.
			const unique = [...new Map(assets.map((asset) => [asset.id, asset])).values()]
			if (unique.length === 0) return

			running.current = true
			setIsPending(true)
			setStartedCount(0)
			setFailedFileNames([])
			setHasFailed(false)

			// A run that throws rather than reporting started nothing, and every asset in it is
			// named: a silent no-op would leave the reader waiting on downloads that never come.
			const outcome = await onDownloadAssets(unique).catch(() => ({
				startedCount: 0,
				failedFileNames: unique.map((asset) => asset.fileName),
			}))

			setStartedCount(outcome.startedCount)
			setFailedFileNames(outcome.failedFileNames)
			setHasFailed(outcome.startedCount === 0)
			setIsPending(false)
			running.current = false
		},
		[onDownloadAssets],
	)

	return { download, isPending, startedCount, failedFileNames, hasFailed, reset }
}
